import React, { useEffect, useState } from "react";
import HRHeader from "../components/HRHeader";
import api from "../api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ConfirmDialog } from "./admin/ConfirmDialog";
import { Sparkles, Calendar, Search, Plus, Trash2, Pencil, GripVertical, Pin, PinOff } from "lucide-react";

const TAG_COLORS = {
  "Beni aziendali": "#0d6cb8", Guida: "#8e44ad", Maturazioni: "#009640",
  "Anzianità": "#e67e22", Welfare: "#16a34a", Onboarding: "#c0392b",
};
const COMMON_TAGS = Object.keys(TAG_COLORS);
const inputCls = "w-full border border-gray-300 rounded-sm px-3 py-2 text-[14px] outline-none focus:border-[#16345c]";
const labelCls = "block text-[12px] font-semibold text-gray-600 mb-1";
const EMPTY = { date: new Date().toISOString().slice(0, 10), tag: "", title: "", desc: "" };

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
  } catch { return iso; }
};

const NovitaPage = () => {
  const { isAdmin } = useAuth();
  const [entries, setEntries] = useState([]);
  const [tag, setTag] = useState("all");
  const [query, setQuery] = useState("");
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);

  const load = () => api.get("/changelog").then((r) => setEntries(r.data?.entries || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title.trim() || !form.date) { toast.error("Inserisci titolo e data"); return; }
    setSaving(true);
    try {
      const payload = { date: form.date, tag: form.tag, title: form.title, desc: form.desc };
      if (form.id) {
        await api.put(`/changelog/${form.id}`, payload);
        toast.success("Novità aggiornata");
      } else {
        await api.post("/changelog", payload);
        toast.success("Novità pubblicata");
      }
      setForm(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    try { await api.delete(`/changelog/${toDelete.id}`); toast.success("Novità eliminata"); setToDelete(null); load(); }
    catch { toast.error("Errore durante l'eliminazione"); }
  };

  const togglePin = async (e) => {
    try {
      const r = await api.put(`/changelog/${e.id}/pin`, { pinned: !e.pinned });
      setEntries(r.data?.entries || entries);
      toast.success(!e.pinned ? "Novità messa in evidenza" : "Evidenza rimossa");
    } catch { toast.error("Errore"); }
  };

  const tags = Array.from(new Set(entries.map((e) => e.tag).filter(Boolean)));
  const q = query.trim().toLowerCase();
  const filtered = entries.filter((e) =>
    (tag === "all" || e.tag === tag) &&
    (!q || `${e.title} ${e.desc} ${e.tag || ""}`.toLowerCase().includes(q))
  );

  const canReorder = isAdmin && tag === "all" && !q;

  const handleDrop = async (targetId) => {
    const src = dragId;
    setDragId(null); setOverId(null);
    if (!src || src === targetId) return;
    const arr = [...entries];
    const from = arr.findIndex((e) => e.id === src);
    const to = arr.findIndex((e) => e.id === targetId);
    if (from < 0 || to < 0) return;
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    setEntries(arr);
    try {
      const r = await api.put("/changelog/reorder", { ids: arr.map((e) => e.id) });
      setEntries(r.data?.entries || arr);
      toast.success("Ordine aggiornato");
    } catch { toast.error("Errore nel riordino"); load(); }
  };

  return (
    <div className="min-h-screen bg-[#f0f2f5]" data-testid="novita-page">
      <HRHeader />
      <main className="max-w-[860px] mx-auto px-4 py-8">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
          <div>
            <h1 className="text-[26px] font-black text-[#16345c] mb-1 flex items-center gap-2"><Sparkles size={24} /> Novità del portale</h1>
            <p className="text-[14px] text-gray-500">Le ultime funzioni aggiunte al portale HR del gruppo Teleborsa, in ordine cronologico.</p>
          </div>
          {isAdmin && (
            <button onClick={() => setForm({ ...EMPTY })} data-testid="novita-add-btn" className="flex items-center gap-2 bg-[#16345c] hover:bg-[#0d1b2f] text-white text-[13px] font-bold px-4 py-2.5 rounded-sm transition-colors"><Plus size={16} /> Aggiungi novità</button>
          )}
        </div>

        <div className="flex items-center border border-gray-300 rounded-sm px-3 bg-white w-full sm:w-[420px] mb-4">
          <Search size={16} className="text-gray-400" />
          <input value={query} data-testid="novita-search" onChange={(e) => setQuery(e.target.value)} placeholder="Cerca una funzione..." className="flex-1 py-2.5 px-2 text-[14px] outline-none" />
          {query && <button onClick={() => setQuery("")} data-testid="novita-search-clear" className="text-[12px] text-gray-400 hover:text-gray-600">Azzera</button>}
        </div>

        {tags.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="novita-filters">
            <button onClick={() => setTag("all")} data-testid="novita-filter-all"
              className={`text-[12.5px] font-semibold rounded-full px-3 py-1.5 border transition-colors ${tag === "all" ? "bg-[#16345c] text-white border-[#16345c]" : "bg-white text-[#16345c] border-gray-200 hover:bg-gray-50"}`}>
              Tutte
            </button>
            {tags.map((t) => {
              const color = TAG_COLORS[t] || "#16345c";
              const on = tag === t;
              return (
                <button key={t} onClick={() => setTag(t)} data-testid={`novita-filter-${t}`}
                  className={`text-[12.5px] font-semibold rounded-full px-3 py-1.5 border transition-colors ${on ? "text-white" : "bg-white hover:bg-gray-50"}`}
                  style={on ? { backgroundColor: color, borderColor: color } : { color, borderColor: `${color}55` }}>
                  {t}
                </button>
              );
            })}
          </div>
        )}

        {canReorder && filtered.length > 1 && (
          <div className="flex items-center gap-2 text-[12.5px] text-gray-500 bg-blue-50 border border-blue-100 rounded-sm px-3 py-2 mb-4" data-testid="novita-reorder-hint">
            <GripVertical size={15} className="text-[#0d6cb8]" /> Trascina le voci per cambiarne l'ordine di visualizzazione.
          </div>
        )}

        {filtered.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-10 text-center text-gray-400 text-[13px]" data-testid="novita-no-results">{q ? `Nessun risultato per "${query}".` : "Nessuna novità da mostrare."}</div>
        ) : (
          <div className="relative pl-6 border-l-2 border-gray-200 space-y-6" data-testid="novita-timeline">
            {filtered.map((e) => {
              const color = TAG_COLORS[e.tag] || "#16345c";
              const dragging = dragId === e.id;
              const isOver = overId === e.id && dragId && dragId !== e.id;
              return (
                <div
                  key={e.id}
                  className="relative"
                  data-testid={`novita-item-${e.id}`}
                  draggable={canReorder}
                  onDragStart={canReorder ? () => setDragId(e.id) : undefined}
                  onDragOver={canReorder ? (ev) => { ev.preventDefault(); setOverId(e.id); } : undefined}
                  onDrop={canReorder ? (ev) => { ev.preventDefault(); handleDrop(e.id); } : undefined}
                  onDragEnd={canReorder ? () => { setDragId(null); setOverId(null); } : undefined}
                >
                  <span className="absolute -left-[31px] top-1.5 w-4 h-4 rounded-full border-4 border-white" style={{ backgroundColor: e.pinned ? "#e0a800" : color }} />
                  <div className={`bg-white border rounded-lg p-5 transition-all ${dragging ? "opacity-40" : "hover:shadow-md"} ${isOver ? "border-[#0d6cb8] ring-2 ring-[#0d6cb8]/30" : e.pinned ? "border-amber-300 ring-1 ring-amber-200 bg-amber-50/40" : "border-gray-200"}`}>
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      {canReorder && <span className="text-gray-300 cursor-grab active:cursor-grabbing" data-testid={`novita-drag-${e.id}`} title="Trascina per riordinare"><GripVertical size={16} /></span>}
                      {e.pinned && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-100 text-amber-700" data-testid={`novita-pinned-badge-${e.id}`}><Pin size={11} /> In evidenza</span>}
                      {e.tag && <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full" style={{ backgroundColor: `${color}1a`, color }}>{e.tag}</span>}
                      <span className="flex items-center gap-1 text-[12px] text-gray-400"><Calendar size={12} /> {fmtDate(e.date)}</span>
                      {isAdmin && (
                        <div className="ml-auto flex items-center gap-1">
                          <button onClick={() => togglePin(e)} data-testid={`novita-pin-${e.id}`} title={e.pinned ? "Rimuovi dall'evidenza" : "Metti in evidenza"} className={`w-7 h-7 inline-flex items-center justify-center rounded-sm transition-colors ${e.pinned ? "text-amber-600 hover:bg-amber-100" : "text-gray-400 hover:bg-gray-100"}`}>{e.pinned ? <PinOff size={14} /> : <Pin size={14} />}</button>
                          <button onClick={() => setForm({ id: e.id, date: e.date, tag: e.tag || "", title: e.title, desc: e.desc || "" })} data-testid={`novita-edit-${e.id}`} title="Modifica novità" className="w-7 h-7 inline-flex items-center justify-center rounded-sm text-[#16345c] hover:bg-blue-50 transition-colors"><Pencil size={14} /></button>
                          <button onClick={() => setToDelete(e)} data-testid={`novita-delete-${e.id}`} title="Elimina novità" className="w-7 h-7 inline-flex items-center justify-center rounded-sm text-[#c00510] hover:bg-red-50 transition-colors"><Trash2 size={14} /></button>
                        </div>
                      )}
                    </div>
                    <h2 className="text-[16px] font-bold text-[#16345c] mb-1">{e.title}</h2>
                    <p className="text-[13.5px] text-gray-600 leading-relaxed">{e.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-[480px]">
          <DialogHeader>
            <DialogTitle>{form?.id ? "Modifica novità" : "Nuova novità"}</DialogTitle>
            <DialogDescription className="sr-only">Modulo per pubblicare o modificare una voce del changelog.</DialogDescription>
          </DialogHeader>
          {form && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Data *</label>
                  <input type="date" value={form.date} data-testid="novita-form-date" onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Categoria</label>
                  <input list="novita-tags" value={form.tag} data-testid="novita-form-tag" onChange={(e) => setForm({ ...form, tag: e.target.value })} placeholder="es. Welfare" className={inputCls} />
                  <datalist id="novita-tags">{COMMON_TAGS.map((t) => <option key={t} value={t} />)}</datalist>
                </div>
              </div>
              <div>
                <label className={labelCls}>Titolo *</label>
                <input value={form.title} data-testid="novita-form-title" onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Descrizione</label>
                <textarea value={form.desc} data-testid="novita-form-desc" onChange={(e) => setForm({ ...form, desc: e.target.value })} rows={3} className={`${inputCls} resize-none`} />
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setForm(null)} className="px-4 py-2 text-[13px] font-semibold text-gray-600 hover:bg-gray-100 rounded-sm transition-colors">Annulla</button>
            <button onClick={save} disabled={saving} data-testid="novita-form-save" className="px-4 py-2 text-[13px] font-bold text-white bg-[#16345c] hover:bg-[#0d1b2f] rounded-sm transition-colors disabled:opacity-60">{saving ? "Salvataggio..." : (form?.id ? "Salva" : "Pubblica")}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!toDelete} onCancel={() => setToDelete(null)} onConfirm={remove} title="Eliminare la novità?">
        Stai per eliminare <b>{toDelete?.title}</b>. Operazione non reversibile.
      </ConfirmDialog>
    </div>
  );
};

export default NovitaPage;
